import { LifecycleRegistry } from '../core/LifecycleRegistry';
import type { FlightInput, FlightMode } from './Flight';

const MOVE_KEYS = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
const FLIGHT_KEYS = [...MOVE_KEYS, 'ShiftLeft', 'ShiftRight', 'Space'];

/** A quiet centre and separate boost thresholds keep a resting thumb from drifting. */
export function stickInput(dx: number, dy: number, radius: number, wasBoosting = false) {
  const length = Math.hypot(dx, dy), amount = Math.min(1, Math.max(0, (length / radius - .14) / .86));
  return {
    x: length ? dx / length * amount : 0,
    y: length ? dy / length * amount : 0,
    boost: -dy > radius * (wasBoosting ? 1.15 : 1.45) && Math.abs(dx) < radius * (wasBoosting ? 1.1 : .8),
  };
}

interface ControlActions {
  look: (x: number, y: number) => void;
  zoom: (delta: number) => void;
  mode: () => FlightMode;
  pause: () => void;
  hint: (message: string) => void;
}
export interface ControlState { locked: boolean; autoForward: boolean; boosting: boolean; touchLayout: boolean }
interface LookPointer { x: number; y: number; startX: number; startY: number; dragging: boolean }

export class Controls {
  private life = new LifecycleRegistry();
  private keys = new Set<string>();
  private pointers = new Map<number, LookPointer>();
  private pinchDistance = 0;
  private stick: { id: number; x: number; y: number; radius: number } | null = null;
  private axes = { x: 0, y: 0, boost: false };
  private knob: HTMLElement;
  private disposed = false;
  private wasLocked = false;
  private lockErrorShown = false;
  captureError = '';
  autoForward = false;
  enabled = true;

  constructor(private stage: HTMLElement, private joystick: HTMLElement, private actions: ControlActions) {
    this.knob = joystick.querySelector<HTMLElement>('span')!;
    this.life.listen(window, 'keydown', event => {
      const target = event.target as HTMLElement;
      if (!this.enabled || /INPUT|SELECT|TEXTAREA/.test(target.tagName) || target.isContentEditable) return;
      if (event.ctrlKey || event.metaKey || event.altKey) { this.keys.clear(); return; }
      if (!FLIGHT_KEYS.includes(event.code) || (target.tagName === 'BUTTON' && event.code === 'Space')) return;
      event.preventDefault(); this.keys.add(event.code);
      if (MOVE_KEYS.includes(event.code) || event.code === 'Space') this.autoForward = false;
      stage.focus({ preventScroll: true });
    });
    this.life.listen(window, 'keyup', event => { this.keys.delete(event.code); });
    this.life.listen(window, 'blur', () => { this.clear(); this.releaseMouse(); actions.pause(); });

    this.life.listen(stage, 'pointerdown', event => {
      if (!this.enabled || this.locked || event.button !== 0 || this.pointers.size >= 2) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY, dragging: false });
      stage.focus({ preventScroll: true }); stage.setPointerCapture(event.pointerId); stage.classList.add('dragging');
      this.pinchDistance = this.distanceBetweenPointers();
    });
    this.life.listen(stage, 'pointermove', event => {
      const pointer = this.pointers.get(event.pointerId);
      if (!pointer || !this.enabled || this.locked) return;
      const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
      pointer.x = event.clientX; pointer.y = event.clientY;
      if (this.pointers.size === 2) {
        const distance = this.distanceBetweenPointers();
        if (this.actions.mode() === 'orbit' && distance > 0 && this.pinchDistance > 0) actions.zoom(-Math.log(distance / this.pinchDistance) * 1000);
        this.pinchDistance = distance;
        return;
      }
      if (!pointer.dragging) {
        if (Math.hypot(pointer.x - pointer.startX, pointer.y - pointer.startY) < 3) return;
        pointer.dragging = true;
      }
      const sensitivity = event.pointerType === 'touch' ? .006 : .003;
      actions.look(dx * sensitivity, dy * sensitivity);
    });
    const endLook = (event: PointerEvent) => {
      this.pointers.delete(event.pointerId); this.pinchDistance = 0;
      if (!this.pointers.size) stage.classList.remove('dragging');
      for (const pointer of this.pointers.values()) { pointer.startX = pointer.x; pointer.startY = pointer.y; pointer.dragging = false; }
    };
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) this.life.listen(stage, name, endLook);
    this.life.listen(stage, 'wheel', event => {
      if (!this.enabled) return;
      event.preventDefault();
      actions.zoom(event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? stage.clientHeight : 1));
    }, { passive: false });

    this.life.listen(joystick, 'pointerdown', event => {
      if (!this.enabled || this.stick || event.button !== 0) return;
      event.preventDefault();
      const rect = joystick.getBoundingClientRect();
      this.stick = { id: event.pointerId, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, radius: rect.width * .34 };
      this.autoForward = false; joystick.setPointerCapture(event.pointerId);
      this.moveStick(event);
    });
    this.life.listen(joystick, 'pointermove', event => this.moveStick(event));
    const endStick = (event: PointerEvent) => { if (this.stick?.id === event.pointerId) this.resetStick(); };
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) this.life.listen(joystick, name, endStick);

    this.life.listen(document, 'mousemove', event => {
      if (this.enabled && this.locked) actions.look(event.movementX * .0022, event.movementY * .0022);
    });
    this.life.listen(document, 'pointerlockchange', () => {
      if (this.locked && (!this.enabled || this.disposed)) { this.releaseMouse(); return; }
      if (!this.locked && this.wasLocked) { this.clear(); actions.pause(); }
      this.wasLocked = this.locked;
    });
    this.life.listen(document, 'pointerlockerror', () => this.lockFallback());
  }

  private distanceBetweenPointers() {
    const [a, b] = [...this.pointers.values()];
    return b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }
  private moveStick(event: PointerEvent) {
    if (!this.enabled || this.stick?.id !== event.pointerId) return;
    this.axes = stickInput(event.clientX - this.stick.x, event.clientY - this.stick.y, this.stick.radius, this.axes.boost);
    this.knob.style.transform = `translate(${this.axes.x * this.stick.radius}px, ${this.axes.y * this.stick.radius}px)`;
    this.joystick.classList.toggle('boosting', this.axes.boost);
    this.joystick.classList.toggle('engaged', Math.hypot(this.axes.x, this.axes.y) > 0);
  }
  private resetStick() {
    const id = this.stick?.id;
    this.stick = null; this.axes = { x: 0, y: 0, boost: false };
    this.knob.style.transform = ''; this.joystick.classList.remove('boosting', 'engaged');
    if (id !== undefined && this.joystick.hasPointerCapture(id)) this.joystick.releasePointerCapture(id);
  }
  get locked() { return document.pointerLockElement === this.stage; }
  get touchLayout() { return window.matchMedia('(any-pointer: coarse)').matches || this.stage.clientWidth <= 760 || (this.stage.clientHeight <= 500 && this.stage.clientWidth <= 1024); }
  get state(): ControlState { return { locked: this.locked, autoForward: this.autoForward, boosting: this.read().boost, touchLayout: this.touchLayout }; }
  async captureMouse() {
    if (!this.enabled || this.locked || this.touchLayout) return;
    this.lockErrorShown = false; this.captureError = '';
    if (!this.stage.requestPointerLock) { this.lockFallback(); return; }
    this.stage.focus({ preventScroll: true });
    try {
      await this.stage.requestPointerLock();
      if (!this.enabled || this.disposed) this.releaseMouse();
    } catch (error) { this.captureError = error instanceof Error ? error.message : String(error); this.lockFallback(); }
  }
  private lockFallback() {
    if (this.disposed || !this.enabled || this.lockErrorShown) return;
    this.lockErrorShown = true; this.actions.hint('可以继续按住画面拖动转向，WASD 移动');
  }
  releaseMouse() { if (this.locked) document.exitPointerLock(); }
  toggleAutoForward() { if (this.enabled) this.autoForward = !this.autoForward; }
  read(): FlightInput {
    if (!this.enabled) return { forward: 0, right: 0, up: 0, boost: false, brake: false };
    const has = (...codes: string[]) => codes.some(code => this.keys.has(code)) ? 1 : 0;
    return {
      forward: has('KeyW', 'ArrowUp') - has('KeyS', 'ArrowDown') - this.axes.y + Number(this.autoForward),
      right: has('KeyD', 'ArrowRight') - has('KeyA', 'ArrowLeft') + this.axes.x,
      up: has('KeyE') - has('KeyQ'), boost: !!has('ShiftLeft', 'ShiftRight') || this.axes.boost, brake: !!has('Space'),
    };
  }
  clear() {
    this.keys.clear(); this.autoForward = false; this.resetStick();
    const ids = [...this.pointers.keys()]; this.pointers.clear(); this.pinchDistance = 0;
    this.stage.classList.remove('dragging');
    for (const id of ids) if (this.stage.hasPointerCapture(id)) this.stage.releasePointerCapture(id);
  }
  get listeners() { return this.life.size; }
  dispose() { this.disposed = true; this.enabled = false; this.clear(); this.releaseMouse(); this.life.release(); }
}
